import React, { useState, useEffect } from "react";
import "./PrivateEvents.css";
import { Helmet } from "react-helmet-async";

import island1 from "../assets/island1-min.webp";
import island2 from "../assets/island2-min.webp";
import island3 from "../assets/island3-min.webp";
import island4 from "../assets/island4-min.webp";
import island5 from "../assets/island5-min.webp";

import mainland1 from "../assets/mainland1-min.webp";
import mainland2 from "../assets/mainland2-min.webp";
import mainland3 from "../assets/mainland3-min.webp";
import mainland4 from "../assets/mainland4-min.webp";
import mainland5 from "../assets/mainland5-min.webp";

function PrivateEventsPage() {
  const islandImages = [island1, island2, island3, island4, island5];
  const mainlandImages = [
    mainland1,
    mainland2,
    mainland3,
    mainland4,
    mainland5,
  ];

  const [index, setIndex] = useState(0);
  const [fade, setFade] = useState(true);

  useEffect(() => {
    const interval = setInterval(() => {
      setFade(false);
      setTimeout(() => {
        setIndex((prev) => (prev + 1) % islandImages.length);
        setFade(true);
      }, 500);
    }, 3000);

    return () => clearInterval(interval);
  }, [islandImages.length]);

  return (
    <div className="private-events-page page-container">
      <Helmet>
        <title>Private Events | LasGidi Restaurant</title>

        <meta
          name="description"
          content="Book LasGidi Restaurant's private event halls for birthdays, weddings, corporate dinners, and celebrations. Premium space, catering, and ambience."
        />

        <meta
          name="keywords"
          content="LasGidi Restaurant Event Hall, Manchester Party Venue, Nigerian Restaurant Events, Hall Booking Manchester, Private Event Space"
        />

        <meta
          property="og:title"
          content="Private Events | LasGidi Restaurant"
        />

        <meta
          property="og:description"
          content="Host your next event at LasGidi Restaurant. Perfect for birthdays, weddings, corporate parties, and private dining experiences."
        />

        <meta property="og:type" content="website" />
      </Helmet>
      
      <div className="events-container">
        <h1>Host Your Events</h1>
      </div>

      {/* Introductory Section */}
      <section className="intro-text">
        <p>
          Make your celebrations unforgettable with Lasgidi Restaurant. From
          corporate gatherings to intimate parties or a grand celebration, we
          have the ideal setting for you. Let us make your event unforgettable.
          <br />
          <br />
          To secure a booking, please review our terms below and contact our events team directly.
        </p>
      </section>

      {/* Our Halls Section */}
      <section className="event-halls">
        <h2 className="halls-heading">- Our Halls -</h2>
        <div className="halls-container">
          <div className={`hall island-hall ${fade ? "fade-in" : "fade-out"}`}>
            <img src={islandImages[index]} alt="Island Hall" />
            <div className="hall-overlay">
              <h2>Island Hall</h2>
            </div>
          </div>
          <div
            className={`hall mainland-hall ${fade ? "fade-in" : "fade-out"}`}
          >
            <img src={mainlandImages[index]} alt="Mainland Hall" />
            <div className="hall-overlay">
              <h2>Mainland Hall</h2>
            </div>
          </div>
        </div>
      </section>

      {/* Terms and Conditions Section */}
      <section className="terms-section">
        <h2 className="halls-heading">- Booking Information -</h2>
        
        <div className="terms-container">
          <p className="agreement-note">By proceeding to make a booking via the contact details below, you agree to the Terms and Conditions listed here.</p>
          
          <h3 className="terms-subheadline">Terms & Conditions</h3>
          
          <div className="terms-grid">
            <div className="terms-item">
              <h3>Booking Policy</h3>
              <p>Bookings are subject to availability. A 50% deposit is required to secure your reservation and the balance must be paid two weeks before the event date. For bookings made less than two weeks before the event, full payment is required.</p>
            </div>
            <div className="terms-item">
              <h3>Use of Facilities</h3>
              <p>Events must finish by 11:30pm, with cleaning allowed until midnight. Weekend hours may be extended by prior agreement. The hall may only be used for the purpose stated in this application.</p>
            </div>
            <div className="terms-item">
              <h3>Noise</h3>
              <p>The applicant is responsible for ensuring that noise levels remain within regulatory limits. Music must stop by 11:30pm on weekdays; weekend hours may be extended by agreement.</p>
            </div>
            <div className="terms-item">
              <h3>Liability</h3>
              <p>The applicant is responsible for any damage to the hall, its equipment, or facilities during the rental period. A refundable security deposit (£100–£200) is required depending on the event type.</p>
            </div>
            <div className="terms-item">
              <h3>Fees</h3>
              <p>The hall rental fee is £700 per day or £100 per hour. Music equipment can be hired at £200 per event.</p>
            </div>
            <div className="terms-item">
              <h3>Cleanliness</h3>
              <p>The hall and car park must be left in the same condition as found. Additional cleaning charges may apply if this condition is not met.</p>
            </div>
            <div className="terms-item">
              <h3>Cancellations</h3>
              <p>Notice of cancellation must be given at least 21 days before the event to receive a refund. Cancellations within 14 days may be subject to partial or no refund.</p>
            </div>
            <div className="terms-item">
              <h3>Alcohol and Smoking</h3>
              <p>Alcohol may only be served with appropriate licensing. Smoking is strictly prohibited within the hall premises.</p>
            </div>
            <div className="terms-item">
              <h3>Decorations</h3>
              <p>Decorations must be approved before use. Nails, staples, adhesives, confetti, and live candles are not permitted. Decorations cannot be done the day before and all items must be removed on the same day.</p>
            </div>
            <div className="terms-item">
              <h3>Security</h3>
              <p>Security personnel must be present to control the car park and maintain order. The cost will be charged to the client or they may provide their own security.</p>
            </div>
            <div className="terms-item">
              <h3>Children</h3>
              <p>Children must be supervised at all times. Keep them within the designated hall as rented. They must not play near the main road unsupervised.</p>
            </div>
            <div className="terms-item">
              <h3>Privacy</h3>
              <p>Your personal data will be used solely for processing your hall rental application and managing your booking. Your information will not be shared with third parties.</p>
            </div>
          </div>
        </div>
      </section>

      <div className="booking-cta-container">
        <p className="booking-instruction-text">To book the hall, please contact us:</p>
        <a
          className="cta-button primary-btn inquire-button post-review-btn"
          href="tel:07733808828"
        >
          Call for Bookings: 07733 808 828
        </a>
      </div>
    </div>
  );
}

export default PrivateEventsPage;
